// THE store-item copy resolver — the one place a store row's human-readable
// `name` / `description` is read for display.
//
// IT LIVES IN `shared/` BECAUSE IT HAS TWO CONSUMERS, NOT ONE. The App Store
// (`packages/marketplace`, which re-exports it from its own barrel so every
// `el.call('storeItemText', …)` site there is unchanged) and the SHELL: an
// installed app contributes its navbar tab + launcher tile from its published
// manifest (`configDefaults.navbar` / `.apps`), and those rows used to print
// the manifest's English literal while the store printed the app's real,
// translated name — "Warehouse" in the navbar, "Inventory / ინვენტარი" in the
// store, for the same record. One name for one app means ONE resolver for both
// surfaces; a second copy in the shell is the drift this file exists to
// prevent. It also has to be EAGER on a shell route: marketplace's own barrel
// folds in only on `/marketplace*`, so a shell-route render site could not
// reach it there at all.
//
// A store row is a LIVE RECORD (`sdk.listPublicProjects({ projectType: … })`,
// `integration_kinds`), so its name and description are CONTENT owned by the
// record, not copy owned by this app. They can never be hardcoded here, and a
// single polyglot key per row would break the moment a third party publishes
// one. So display copy resolves from three sources per field, English LAST.
//
// `name`:
//   1. `item.i18n[lang].name` — the record's own localized content. No
//      endpoint serves this field today; it is the path a third-party
//      publisher takes once the record carries it, and the reason this
//      resolver is ordered rather than a plain polyglot lookup.
//   2. `marketplace.app.<slug>.name` — a polyglot key, for the store apps
//      Symbols itself ships. `translate()` returns its INPUT unchanged when the
//      key is unknown, so a row nobody has translated falls straight through
//      to (3) with no branch here.
//   3. `item.name` — the copy the record already carries (see `description` 3).
//
// `description` — THE APP'S ONE LINE (Nika 2026-10-31, field choice A: one
// text per app, on every surface):
//   1. the App Store listing's tagline (`metadata.store.tagline`,
//      docs/APP_STORE_LISTING.md §2) in the session language, else English,
//      else the first language it has — the rule of the store's own
//      `appStoreText`, so the store page's lead line and every card, row and
//      caption read the same words. There is no shared polyglot line for an
//      app any more (`marketplace.app.<slug>.descr` is gone): the listing the
//      app ships beside its name is its one source.
//   2. `item.i18n[lang].description` — the record's own localized content.
//   3. `item.description` — the copy the record already carries, passed
//      through `translate()` once so a SEED row that stores a polyglot key in
//      that field resolves and a LIVE row's real prose comes back unchanged.
//
// There is never a fourth source and never a per-app special case: a render
// site that reads `item.name` / `item.description` directly is the defect this
// file exists to prevent.
//
// `<slug>` is derived from the record identity (`item.key`, else the last
// segment of `item.id`), NOT from a hand-kept map — a map is a per-app special
// case with extra steps. Two records that fold to the same slug share one key
// space by design; the record's own i18n (source 1) outranks it either way.
//
// A CALLER NEED NOT HOLD A STORE ROW. The contract is `{ key, name }` /
// `{ key, description }` — anything carrying the record's identity plus the
// copy it already has. The shell's navbar/launcher rows pass
// `{ key: row.appKey, name: row.label }` (functions/workspaceConfigs.js stamps
// `appKey` from the installed app's `appId`), so a house app resolves through
// `name` 2 and a third party's app, an extension, or a customer's own renamed
// tab falls through `name` 3 untouched — `translate()` returns its input
// unchanged for an unknown key, so none of that needs a branch here. A caller
// that carries no `store` at all (the /add-app card, the search caption, the
// welcome's app rows) reads the listing of the App Store row with the same
// key in the catalogue the shell already loaded (`root._wsAppsCatalog`, whose
// rows carry `store: { tagline }` — workspace functions/workspaceApps.js). A
// row that says `store: null` has no listing and borrows none, and an item of
// another kind (an integration, a template) never borrows an app's.

// One key segment out of a record identity: `cap-table-app` -> `capTableApp`.
// Key segments are lowerCamelCase (docs/I18N-CONVENTION.md §4) and every i18n
// gate matches `[A-Za-z0-9]`, so a hyphen, underscore or dot in a project key
// has to fold into a camel hump rather than survive into the key.
const _storeTextSlugSegment = (raw) => {
  const parts = String(raw || '')
    .split(/[^A-Za-z0-9]+/)
    .filter(Boolean)
  if (!parts.length) return ''
  const [head, ...rest] = parts
  if (!/^[A-Za-z]/.test(head)) return ''
  return (
    head.charAt(0).toLowerCase() +
    head.slice(1) +
    rest.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('')
  )
}

// The slug a store item's polyglot keys hang off. Exported for the i18n gate,
// which asserts every shipped `marketplace.app.*` key belongs to a real slug —
// no render site calls this, they all call `storeItemText`.
export const storeItemSlug = (item) => {
  if (!item) return ''
  const raw =
    item.key ||
    String(item.id || '')
      .split('.')
      .pop() ||
    ''
  return _storeTextSlugSegment(raw)
}

// The active interface language, through polyglot's OWN resolver — never a
// second hand-written `root.lang > defaultLang > 'en'` chain (a duplicated
// precedence rule always drifts; smbls fixed exactly that in
// `plugins/polyglot/index.js`).
const _storeTextLang = (el) => {
  if (!el || typeof el.call !== 'function') return 'en'
  return el.call('getActiveLang') || 'en'
}

// `field` is the RECORD field: 'name' or 'description'. Anything else is data,
// not display copy, and is returned untranslated.
//
// Call it as `el.call('storeItemText', item, 'name')` — `el.call` binds
// `this` = el, so a render site passes no element. A plain function that
// already holds an element passes it third: `storeItemText(item, 'name', el)`.
export const storeItemText = function (item, field, el) {
  if (!item || !field) return ''
  const source = el || this
  if (field !== 'name' && field !== 'description') return item[field] || ''
  const lang = _storeTextLang(source)

  // description 1 — the App Store listing's tagline: the item's own listing,
  // else (a caller that carries no `store`) the catalogue row's with this key.
  if (field === 'description') {
    const borrows = item.store === undefined && !!item.key && (!item.kind || item.kind === 'app')
    const state = borrows && source ? source.state : null
    const root = state ? state.root || state : null
    const want = borrows ? String(item.key).trim().toLowerCase() : ''
    const row =
      root && Array.isArray(root._wsAppsCatalog)
        ? root._wsAppsCatalog.find((r) => r && r.key && String(r.key).trim().toLowerCase() === want)
        : null
    const store = borrows ? row && row.store : item.store
    const tagline = store && typeof store === 'object' ? store.tagline : null
    if (typeof tagline === 'string' && tagline.trim()) return tagline.trim()
    if (tagline && typeof tagline === 'object') {
      const said = [tagline[lang], tagline.en, ...Object.values(tagline)].find(
        (v) => typeof v === 'string' && v.trim()
      )
      if (said) return said.trim()
    }
  }

  // name 1 / description 2 — the record's own localized content.
  const localized = item.i18n?.[lang]?.[field]
  if (typeof localized === 'string' && localized.trim()) return localized

  // name 2 — a polyglot key, for the store apps we ship.
  const slug = field === 'name' ? storeItemSlug(item) : ''
  if (slug && source && typeof source.call === 'function') {
    const key = `marketplace.app.${slug}.name`
    const translated = source.call('polyglot', key)
    if (translated && translated !== key) return translated
  }

  // 3 — the copy the record already carries. On a LIVE row that is real
  // English prose. On a SEED row it is a polyglot key the record itself
  // carries (`apps.catalog.<id>.descr` — packages/shared/functions/
  // marketplaceCatalog.js keys its 31 descriptions the key-in-state way), so
  // it goes through `polyglot` too. `translate()` returns its input unchanged
  // when nothing resolves, so prose is untouched and a key never reaches the
  // screen raw.
  const own = item[field] || ''
  if (!own || !source || typeof source.call !== 'function') return own
  return source.call('polyglot', own)
}
